/**
 * QueuesHome — the Queues tab of the Queues section (route `queues`), on the
 * Events-section list paradigm; the LIST view matches Event Hubs (full-width
 * card table, checkbox selection + bulk bar, status edge, identity block).
 * CREATE runs through the Journey designer (`newjourneys?new=queue`, 2026-08-25 —
 * the same entry-point pattern as Add Event); EDIT opens the same designer on the
 * queue's own journey document (`newjourneys?journey=queue:<id>`, 2026-08-26 —
 * the standalone queue wizard was removed).
 * Queue types are development-defined — no admin management surface.
 * WRITES are AWAITED typed ops (no fake interactivity).
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { alpha, Btn, Card, Checkbox, Icon, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { StatTile, FilterBar, FilterDropdown, EmptyState, RowActions } from "../../shell/composites";
import { useViewMode, ViewToggle, CardActionBar, CARD_GRID, useRowSelection, BulkActionBar } from "../../shell/list-composites";
import { useIsMobile } from "../../shell/useResponsive";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import { can } from "../../shell/access";
import { useWfResource, useBusy } from "../workforce/useWorkforce";
import { LocationFilter, WfPending } from "../workforce/wf-shared";
import { listQueues, listQueueTypes, createQueue, updateQueue, setQueueStatus, deleteQueue, type QueueSummary, type QueueType, type QueueStatus, type QueueListPage } from "./queues-api";
import { qHoursLabel } from "./queue-hours";
import { ResolvesHoldsChips } from "../../lib/holds";
import { queuesSectionTabsOrNone, useQueueSectionCounts, SORT_ARROW } from "./queues-section";
import { useTenantPublicConfig } from "../../lib/public-url";
import { queueJoinUrl } from "./queue-join-url";
import { copyText } from "../../lib/copy-text";
import { Modal } from "../../shell/Overlay";

import { useT } from "@v0y4ge/i18n-runtime";
const statusMeta = (t: (key: string, fallback: string) => string): Record<QueueStatus, { tone: "success" | "warn" | "neutral"; label: string; edge: string }> => ({
  open: { tone: "success", label: t("queues.home.statusOpen", "Open"), edge: T.green1 },
  paused: { tone: "warn", label: t("queues.home.statusPaused", "Paused"), edge: T.orange1 },
  closed: { tone: "neutral", label: t("queues.home.statusClosed", "Closed"), edge: T.n4 },
});
const columnDefs = (t: (key: string, fallback: string) => string): { k: string; label: string }[] => [
  /* "Type" (quick vs standard) removed as a COLUMN 2026-09-08 (operator) — it is
     assigned positionally by the seed layer rather than authored per desk, so it
     carried no information a user could act on. typeId still drives the row tile
     colour and the card-view pill, and the Any-type filter still works. */
  { k: "name", label: t("queues.home.colQueue", "Queue") }, { k: "status", label: t("queues.home.colStatus", "Status") },
  { k: "waiting", label: t("queues.home.colWaiting", "Waiting") }, { k: "avgWait", label: t("queues.home.colAvgWait", "Avg wait") }, { k: "longest", label: t("queues.home.colLongest", "Longest") },
  { k: "staffed", label: t("queues.home.colStaffed", "Staffed") }, { k: "hours", label: t("queues.home.colHours", "Hours") },
];
const thStyle: CSSProperties = { textAlign: "left", padding: "10px 16px", whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" };
const tdStyle: CSSProperties = { padding: "14px 16px", verticalAlign: "middle" };

export default function QueuesHome() {
  const t = useT();
  const STATUS_META = statusMeta(t);
  const COLUMNS = columnDefs(t);
  const nav = useNav();
  const isMobile = useIsMobile();
  const counts = useQueueSectionCounts();
  const [locationId, setLocationId] = useState<string | null>(null);
  const { data, loading, error, reload } = useWfResource<QueueListPage>(`queues|${locationId ?? "all"}`, () => listQueues(locationId), { pollMs: 30_000 });
  const { data: typesData } = useWfResource<{ types: QueueType[] }>("qtypes", () => listQueueTypes());
  const { busy, run } = useBusy();
  const [view, setView] = useViewMode("queues", isMobile ? "grid" : "list");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [sort, setSort] = useState("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  useTenantPublicConfig(); // warm the tenant public-host cache for the join links
  /** Pause / Close ask first (2026-09-15, PP): both take a live queue away from
   *  customers mid-wait, so neither fires straight off the menu. Open/Resume
   *  stays immediate — it only gives capacity back. */
  const [confirmStatus, setConfirmStatus] = useState<null | { status: "paused" | "closed"; targets: QueueSummary[]; bulk: boolean }>(null);
  /* Delete queue (JAI-1700): the dialog's target, and what the server said. A
     queue with history is CLOSED, never deleted, so a 409 turns the dialog into
     "close it instead" rather than failing silently. */
  const [askDelete, setAskDelete] = useState<null | { q: QueueSummary; busy: boolean; refusal: null | { reason: "entries" | "linked_events"; linkedEvents: number; message: string } }>(null);
  /* The dialog as it is NOW, so a result that lands after the user closed it is
     ignored instead of reopening it (CC 0013 §4). */
  const askDeleteRef = useRef(askDelete);
  useEffect(() => { askDeleteRef.current = askDelete; }, [askDelete]);
  const runDelete = async () => {
    if (!askDelete) return;
    const target = askDelete.q;
    const stillOpen = () => !!askDeleteRef.current && askDeleteRef.current.q.id === target.id;
    setAskDelete({ ...askDelete, busy: true });
    try {
      await deleteQueue(target.id);
      if (stillOpen()) setAskDelete(null);
      toast({ tone: "success", title: t("queues.home.queueDeleted", "Queue deleted"), subtitle: target.name });
      reload();
    } catch (e) {
      const err = e as { code?: string; status?: number; message?: string; params?: { reason?: string; linkedEvents?: number } } | null;
      // Already gone is the outcome the user asked for: say so and reload.
      if (err?.status === 404 || err?.code === "QUEUE_NOT_FOUND") {
        if (stillOpen()) setAskDelete(null);
        toast({ tone: "success", title: t("queues.home.queueAlreadyDeleted", "Queue already deleted"), subtitle: target.name });
        reload();
        return;
      }
      if (!stillOpen()) return;
      if (err?.code === "QUEUE_DELETE_CONFLICT") {
        setAskDelete({ ...askDelete, busy: false, refusal: {
          reason: err.params?.reason === "linked_events" ? "linked_events" : "entries",
          linkedEvents: Number(err.params?.linkedEvents) || 0,
          message: err.message || t("queues.home.deleteConflict", "This queue cannot be deleted: it has taken customers, or an event still feeds it. Close it instead."),
        } });
      } else {
        setAskDelete({ ...askDelete, busy: false });
        toast({ tone: "danger", title: t("queues.home.deleteFailed", "Couldn't delete the queue"), subtitle: err?.message || askDelete.q.name });
      }
    }
  };

  const types = typesData?.types ?? [];
  const queues = data?.data ?? [];
  const typeLabel = (id: string) => types.find((t) => t.id === id)?.label ?? id;

  const onHeaderSort = (k: string) => {
    if (sort === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(k); setSortDir(["waiting", "avgWait", "longest"].includes(k) ? "desc" : "asc"); }
  };
  const filtered = useMemo(() => {
    const qy = query.trim().toLowerCase();
    let rows = queues.filter((q) =>
      (!qy || q.name.toLowerCase().includes(qy) || (q.locationName ?? "").toLowerCase().includes(qy) || (q.services ?? []).join(" ").toLowerCase().includes(qy)) &&
      (!statusFilter || STATUS_META[q.status].label === statusFilter) &&
      (!typeFilter || typeLabel(q.typeId) === typeFilter));
    const val = (q: QueueSummary): string | number => sort === "status" ? q.status
      : sort === "waiting" ? q.waiting : sort === "avgWait" ? q.avgWaitMins : sort === "longest" ? q.longestWaitMins
      : sort === "staffed" ? q.staffed : sort === "hours" ? qHoursLabel(q.hours, q.hoursAuthored, { timeZone: q.locationTimezone, ignoreHours: q.ignoreHours }) : q.name.toLowerCase();
    rows = [...rows].sort((a, b) => { const x = val(a), y = val(b); const base = x < y ? -1 : x > y ? 1 : 0; return sortDir === "desc" ? -base : base; });
    return rows;
  }, [queues, query, statusFilter, typeFilter, sort, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  const selection = useRowSelection(filtered.map((q) => q.id));

  const totals = useMemo(() => ({
    waiting: queues.reduce((s, q) => s + q.waiting, 0),
    avgWait: queues.length ? Math.round(queues.reduce((s, q) => s + q.avgWaitMins, 0) / queues.length) : 0,
    longest: queues.reduce((s, q) => Math.max(s, q.longestWaitMins), 0),
    open: queues.filter((q) => q.status === "open").length,
  }), [queues]);

  const changeStatus = (q: QueueSummary, status: QueueStatus) => run(`st:${q.id}`, async () => {
    await setQueueStatus(q.id, status);
    const statusLabel = STATUS_META[status].label.toLowerCase();
    toast({ tone: "success", title: t("queues.home.toastStatusChangedTitle", "Queue {status}").replace("{status}", statusLabel), subtitle: t("queues.home.toastStatusChangedSubtitle", "“{name}” is now {status}.").replace("{name}", q.name).replace("{status}", statusLabel) }); // ui-config — "{…}" strings are message-placeholder tokens, not copy
    reload();
  });
  const bulkStatus = (status: QueueStatus) => run(`bulk:${status}`, async () => {
    const targets = filtered.filter((q) => selection.selected.has(q.id) && q.status !== status);
    await Promise.all(targets.map((q) => setQueueStatus(q.id, status)));
    toast({ tone: "success", title: t("queues.home.toastBulkStatusChangedTitle", "{n, plural, one {# queue} other {# queues}} {status}", { n: targets.length, status: STATUS_META[status].label.toLowerCase() }) });
    selection.clear();
    reload();
  });
  const clone = (q: QueueSummary) => run(`cl:${q.id}`, async () => {
    const created = await createQueue({ name: t("queues.copyName", "{name} (copy)", { name: q.name }), typeId: q.typeId, locationId: q.locationId, services: q.services, slaTargetMins: q.slaTargetMins, ticketPrefix: q.ticketPrefix });
    // The embedded journey travels with the clone (deep copy — the queues diverge freely afterwards).
    /* `ignoreHours` deliberately NOT copied (CD, 2026-09-18): it is an
       exception granted to one desk for a demo or an odd day, not a property of
       the desk's design. A duplicate comes up hours-enforced, so nobody
       inherits an always-open door they did not ask for. */
    await updateQueue(created.id, { hours: q.hours, hoursAuthored: q.hoursAuthored, assignedStaff: q.assignedStaff, resources: q.resources, needed: q.needed, journey: q.journey ? JSON.parse(JSON.stringify(q.journey)) : null, journeySource: q.journey ? t("queues.home.journeySourceCopiedFrom", "Copied from “{name}”").replace("{name}", q.name) : q.journeySource });
    toast({ tone: "success", title: t("queues.home.toastQueueClonedTitle", "Queue cloned"), subtitle: t("queues.home.toastQueueClonedSubtitle", "“{name} (copy)” created — it starts closed until you open it.").replace("{name}", q.name) }); // ui-config — "{…}" strings are message-placeholder tokens, not copy
    reload();
  });
  const copyJoin = (q: QueueSummary) => {
    /* The canonical customer link, not this Studio's origin (2026-09-15, PP).
       It was `${location.origin}/queue/join?q=<id>` — an ADMIN url that only
       works for a signed-in operator inside the app shell. The customer surface
       lives on the tenant's served join authority (`joinHost`, e.g.
       https://join.<tenant-host>) with the desk in the QUERY STRING —
       `queueJoinUrl` is the one composer the designer's Publish step and its QR
       also use, so a link copied here and a QR printed there are one link.
       `publicHost("join")` is SERVED, never composed: empty means the tenant has
       no join surface resolved yet, and we say so rather than copy a dead url. */
    /* Host root + `?queue=<id>` (2026-09-17, CD). The worker-rendered pages at
       `/queues`, `/queues/<id>/join`, `/<id>` and `/<id>/join` are being removed
       from every public host; the journey then owns every path, and a bare path
       segment carrying the join CODE resolves to its not-found.

       The join-CODE gate added on 2026-09-16 is therefore GONE, and deliberately
       so: it existed because `publicUrl(joinCode)` minted a dead link when a
       queue had no code, and a queue created through the admin flow has none by
       default. The link now carries `q.id`, which every queue always has, so
       that reason has evaporated — keeping the gate would withhold a WORKING
       link from exactly the default-path queue it was added to protect. The
       host guard stays, because an unresolved join host is still no surface. */
    const url = queueJoinUrl(q.id);
    if (!url) {
      toast({
        tone: "danger",
        title: t("queues.home.toastJoinHostUnresolvedTitle", "No join link yet"),
        subtitle: t("queues.home.toastJoinHostUnresolvedSubtitle", "This tenant's join host hasn't resolved — publish the queue's journey, then try again."),
      });
      return;
    }
    void copyText(url).then((ok) => ok
      ? toast({ tone: "success", title: t("queues.home.toastJoinLinkCopiedTitle", "Join link copied"), subtitle: url })
      : toast({ tone: "danger", title: t("queues.home.toastCopyFailedTitle", "Couldn't copy the link"), subtitle: url }));
  };

  const moreItems = (q: QueueSummary) => [
    ...(can("queuehealth.read") ? [{ icon: <Icon.chart size={14} />, label: t("queues.home.menuQueueHealth", "Queue health"), onClick: () => nav.go("queues.health", { q: q.id }) }] : []),
    { icon: <Icon.copy size={14} />, label: t("queues.home.menuCopyJoinLink", "Copy join link"), onClick: () => copyJoin(q) },
    ...(can("queue.serve") ? [{ icon: <Icon.check size={14} />, label: t("queues.home.menuOpenConcierge", "Open Concierge"), onClick: () => nav.go("queues.console", { q: q.id }) }] : []),
    /* Gated on `queues.write`, the capability the op actually authorizes
       (2026-09-15, PP). It read `queues.publish`, which every demo role holds
       alongside write — so nothing broke — but a publish-only role would have
       seen Pause/Close/Resume and taken a 403 from
       `setQueueStatus` (contract: capability `queues.write`). */
    ...(can("queues.write") ? [
      "divider" as const,
      q.status === "open"
        ? { icon: <Icon.clock size={14} />, label: t("queues.home.menuPauseQueue", "Pause queue"), onClick: () => setConfirmStatus({ status: "paused", targets: [q], bulk: false }) }
        : { icon: <Icon.refresh size={14} />, label: q.status === "paused" ? t("queues.home.menuResumeQueue", "Resume queue") : t("queues.home.menuOpenQueue", "Open queue"), onClick: () => changeStatus(q, "open") },
      ...(q.status !== "closed" ? [{ icon: <Icon.x size={14} />, label: t("queues.home.menuCloseQueue", "Close queue"), danger: true, onClick: () => setConfirmStatus({ status: "closed", targets: [q], bulk: false }) }] : []),
      { icon: <Icon.trash size={14} />, label: t("queues.home.menuDeleteQueue", "Delete queue"), danger: true, onClick: () => setAskDelete({ q, busy: false, refusal: null }) },
    ] : []),
  ];
  const actions = (q: QueueSummary) => ({
    // Eye action = the customer-facing journey preview — the SAME view as the
    // Live tab's Preview button (interactive join flow).
    onView: () => nav.go("queue.join", { q: q.id }),
    viewLabel: t("queues.home.actionViewPreview", "View preview"),
    onEdit: can("queues.write") ? () => nav.go("newjourneys", { journey: `queue:${q.id}` }) : undefined,
    editLabel: t("queues.home.actionEditQueue", "Edit queue"),
    onDuplicate: can("queues.write") ? () => clone(q) : undefined,
    duplicateLabel: t("queues.home.actionCloneQueue", "Clone queue"),
    moreHeader: q.name,
    moreItems: moreItems(q),
  });

  const filtersActive = !!(query || statusFilter || typeFilter);
  const openRow = (q: QueueSummary) => can("queues.write") ? nav.go("newjourneys", { journey: `queue:${q.id}` }) : nav.go("queues.console", { q: q.id });

  const listView = (
    <Card style={{ padding: 0 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 13 }}>
        <thead>
          <tr style={{ background: T.n6, color: T.soft, fontSize: 11, textTransform: "uppercase", fontWeight: 600 }}>
            <th style={{ ...thStyle, width: 36, paddingRight: 0, cursor: "default", boxSizing: "content-box" }}>
              <Checkbox checked={selection.allChecked} indeterminate={selection.someChecked} onChange={selection.toggleAll} />
            </th>
            {COLUMNS.map((c) => (
              <th key={c.k} style={thStyle} onClick={() => onHeaderSort(c.k)} aria-sort={sort === c.k ? (sortDir === "asc" ? "ascending" : "descending") : undefined}>
                {c.label}{SORT_ARROW(sort === c.k, sortDir)}
              </th>
            ))}
            <th style={{ ...thStyle, cursor: "default", width: 150 }}>{t("queues.home.text", "Actions")}</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((q, i) => {
            const overSla = q.avgWaitMins > q.slaTargetMins && q.status === "open";
            return (
              <tr key={q.id} data-testid="queues-table-row" data-id={q.id} onClick={() => openRow(q)}
                style={{ borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer", background: selection.selected.has(q.id) ? T.p4 : T.white, borderLeft: `3px solid ${STATUS_META[q.status].edge}` }}>
                <td style={{ ...tdStyle, paddingRight: 0 }} onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={selection.selected.has(q.id)} onChange={() => selection.toggle(q.id)} />
                </td>
                <td style={tdStyle}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <div style={{ width: 44, height: 44, borderRadius: 8, background: q.typeId === "quick" ? alpha(T.tDarkBlue, 90) : alpha(T.p1, 90), color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.users size={18} /></div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: T.n1, fontWeight: 600 }}>{q.name}</div>
                      <div style={{ color: T.soft, fontSize: 11 }}>{q.locationName ?? ""}{(q.services ?? []).length ? t("queues.home.text13", "· {arg1}", { arg1: (q.services ?? []).join(", ") }) : ""}</div>
                    </div>
                  </div>
                </td>
                <td style={tdStyle}><Pill tone={STATUS_META[q.status].tone} dot>{STATUS_META[q.status].label}</Pill></td>
                <td style={{ ...tdStyle, color: T.n1, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{q.waiting}</td>
                <td style={{ ...tdStyle, fontVariantNumeric: "tabular-nums" }}>
                  <div style={{ color: overSla ? T.orange1 : T.n1, fontWeight: 600 }}>{q.avgWaitMins}{t("queues.home.text3", "m")}</div>
                  <div style={{ fontSize: 11, color: T.soft }}>{t("queues.home.text4", "target")}{" "}{q.slaTargetMins}{t("queues.home.text3", "m")}</div>
                </td>
                <td style={{ ...tdStyle, color: q.longestWaitMins > q.slaTargetMins * 1.5 ? T.red1 : T.n1, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{q.longestWaitMins}{t("queues.home.text3", "m")}</td>
                <td style={{ ...tdStyle, color: q.staffed < q.needed ? T.red1 : T.n1, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{q.staffed}/{q.needed}</td>
                <td style={{ ...tdStyle, color: T.soft, fontSize: 12, maxWidth: 220 }}>{qHoursLabel(q.hours, q.hoursAuthored, { timeZone: q.locationTimezone, ignoreHours: q.ignoreHours })}</td>
                <td style={tdStyle} onClick={(e) => e.stopPropagation()}><RowActions {...actions(q)} testid="queues-row" /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );

  const cardView = (
    <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 14 }}>
      {filtered.map((q) => {
        const overSla = q.avgWaitMins > q.slaTargetMins && q.status === "open";
        return (
          <Card key={q.id} data-testid="queues-card" data-id={q.id} style={{ padding: 0, overflow: "hidden", display: "flex", flexDirection: "column", cursor: "pointer" }} onClick={() => openRow(q)}>
            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10, flex: 1, borderLeft: `3px solid ${STATUS_META[q.status].edge}` }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{q.name}</div>
                  <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{q.locationName ?? ""}{(q.services ?? []).length ? t("queues.home.text13", "· {arg1}", { arg1: (q.services ?? []).join(", ") }) : ""}</div>
                  <div style={{ fontSize: 11.5, color: T.soft, marginTop: 2, display: "flex", alignItems: "center", gap: 5 }}><Icon.clock size={11}/> {qHoursLabel(q.hours, q.hoursAuthored, { timeZone: q.locationTimezone, ignoreHours: q.ignoreHours })}</div>
                  {/* JAI-1767 — chips only when the desk is linked to a hold. */}
                  <ResolvesHoldsChips value={q.resolvesHolds} t={t} style={{ marginTop: 6 }} />
                </div>
                <Pill tone={q.typeId === "quick" ? "info" : "primary"}>{typeLabel(q.typeId)}</Pill>
                <Pill tone={STATUS_META[q.status].tone} dot>{STATUS_META[q.status].label}</Pill>
              </div>
              <div style={{ display: "flex", gap: 16, fontSize: 12.5, color: T.soft, flexWrap: "wrap" }}>
                <span><b style={{ color: T.n1, fontFamily: T.fontMono, fontSize: 15 }}>{q.waiting}</b> {t("queues.home.text5", "waiting")}</span>
                <span>{t("queues.home.text6", "avg")}{" "}<b style={{ color: overSla ? T.orange1 : T.n1, fontFamily: T.fontMono }}>{q.avgWaitMins}{t("queues.home.text3", "m")}</b> / {q.slaTargetMins}{t("queues.home.text3", "m")}</span>
                <span>{t("queues.home.text7", "longest")}{" "}<b style={{ color: q.longestWaitMins > q.slaTargetMins * 1.5 ? T.red1 : T.n1, fontFamily: T.fontMono }}>{q.longestWaitMins}{t("queues.home.text3", "m")}</b></span>
                <span style={{ color: q.staffed < q.needed ? T.red1 : T.soft }}>{q.staffed}/{q.needed} {t("queues.home.text8", "staffed")}</span>
              </div>
            </div>
            <CardActionBar left={<span>{q.servedToday} {t("queues.home.text9", "served today")}</span>} {...actions(q)} testid="queues-card" />
          </Card>
        );
      })}
    </div>
  );

  return (
    <Screen rail="q-board" tabs={queuesSectionTabsOrNone(nav, "queues", counts)} activeTab="queues">
      <PageHeader title={t("queues.home.title4", "Queues")} subtitle={t("queues.home.subtitle", "Every queue you run — live load, configuration and health.")} breadcrumb={null}
        actions={<>
          {can("queues.write") ? <Btn icon={<Icon.plus size={14}/>} onClick={() => nav.go("newjourneys", { new: "queue" })} testid="queues-new-btn">{t("queues.home.text10", "New queue")}</Btn> : null}
        </>} />
      <DataSourceStrip screen="queues" ops={["listQueues", "listQueueTypes", "createQueue", "updateQueue", "setQueueStatus"]} />
      <div style={{ padding: "16px 24px 0", display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 12 }}>
        <StatTile label={t("queues.home.label12", "Waiting now")} value={totals.waiting} icon={<Icon.users size={14}/>} accent={totals.waiting > 0 ? T.orange1 : T.green1} />
        <StatTile label={t("queues.home.label8", "Avg wait")} value={`${totals.avgWait}m`} icon={<Icon.clock size={14}/>} accent={T.tDarkBlue} />
        <StatTile label={t("queues.home.label13", "Longest wait")} value={`${totals.longest}m`} icon={<Icon.warn size={14}/>} accent={totals.longest > 20 ? T.red1 : T.n2} />
        <StatTile label={t("queues.home.label14", "Open queues")} value={`${totals.open}/${queues.length}`} icon={<Icon.list size={14}/>} accent={T.p1} />
      </div>
      <FilterBar>
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("queues.home.placeholder", "Search queues…")} aria-label={t("queues.home.ariaLabel", "Search queues")} data-testid="queues-search-input"
          style={{ height: 34, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 8, fontSize: 13, fontFamily: T.font, width: 240 }} />
        <FilterDropdown label={statusFilter || t("queues.home.filterAnyStatus", "Any status")} active={!!statusFilter} options={[t("queues.home.statusOpen", "Open"), t("queues.home.statusPaused", "Paused"), t("queues.home.statusClosed", "Closed")]} onPick={setStatusFilter} onClear={() => setStatusFilter(null)} />
        <FilterDropdown label={typeFilter || t("queues.home.filterAnyType", "Any type")} active={!!typeFilter} options={types.map((t) => t.label)} onPick={setTypeFilter} onClear={() => setTypeFilter(null)} />
        <LocationFilter value={locationId} onChange={setLocationId} />
        {filtersActive ? <button onClick={() => { setQuery(""); setStatusFilter(null); setTypeFilter(null); }} style={{ padding: "6px 10px", borderRadius: 999, fontSize: 12, fontWeight: 500, background: "transparent", color: T.p1, border: "none", cursor: "pointer", fontFamily: T.font }}>{t("queues.home.text11", "Clear filters")}</button> : null}
        <span style={{ marginLeft: "auto" }} />
        <ViewToggle mode={view} onChange={setView} testid="queues-view" />
      </FilterBar>

      {can("queues.write") ? (
        <BulkActionBar count={selection.selected.size} noun="queue" onClear={selection.clear}
          actions={[
            { label: t("queues.home.bulkOpen", "Open"), icon: <Icon.refresh size={12} />, onClick: () => bulkStatus("open") },
            { label: t("queues.home.bulkPause", "Pause"), icon: <Icon.clock size={12} />, onClick: () => setConfirmStatus({ status: "paused", targets: filtered.filter((q) => selection.selected.has(q.id) && q.status !== "paused"), bulk: true }) },
            { label: t("queues.home.bulkClose", "Close"), icon: <Icon.x size={12} />, danger: true, onClick: () => setConfirmStatus({ status: "closed", targets: filtered.filter((q) => selection.selected.has(q.id) && q.status !== "closed"), bulk: true }) },
          ]} />
      ) : null}

      <div style={{ padding: "0 24px 24px", flex: 1, overflow: "auto" }}>
        {error ? <WfPending screen="queues" opName="listQueues" error={error} onRetry={reload} />
        : loading ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("queues.home.text12", "Loading queues…")}</div>
        : filtered.length === 0 ? (
          filtersActive
            ? <EmptyState icon={<Icon.list size={20}/>} title={t("queues.home.emptyFilteredTitle", "No queues match these filters")} body={t("queues.home.emptyFilteredBody", "Try clearing a filter or widening the search.")} action={<Btn variant="secondary" onClick={() => { setQuery(""); setStatusFilter(null); setTypeFilter(null); }}>{t("queues.home.text11", "Clear filters")}</Btn>} />
            : <EmptyState icon={<Icon.list size={20}/>} title={t("queues.home.emptyNoneTitle", "No queues at this location")} body={t("queues.home.emptyNoneBody", "Create a queue to start guiding walk-in customers through their visit.")} action={can("queues.write") ? <Btn onClick={() => nav.go("newjourneys", { new: "queue" })}>{t("queues.home.text10", "New queue")}</Btn> : undefined} />
        ) : view === "list" && !isMobile ? listView : cardView}
      </div>

      {/* Pause / Close confirmation (2026-09-15, PP). Both are live-customer
          actions: pausing stops new joins while people are still waiting, and
          closing ends the line. The dialog states the count of people affected,
          because that is the fact the decision turns on, and it names the
          Concierge's own pause/close flow for the cases that need to move or
          notify those people \u2014 this menu is the quick path, not a replacement. */}
      {askDelete ? (
        <Modal testid="queues-delete-confirm-modal" tone="danger" onClose={() => setAskDelete(null)}
          title={askDelete.refusal ? t("queues.home.cannotDeleteTitle", "This queue can't be deleted") : t("queues.home.deleteQueueTitle", "Delete queue?")}
          sub={askDelete.q.name}
          icon={<Icon.trash size={16} />}
          footer={askDelete.refusal
            ? <>
                <Btn variant="secondary" onClick={() => setAskDelete(null)}>{t("common.actions.cancel", "Cancel")}</Btn>
                {askDelete.refusal.reason === "entries" && askDelete.q.status !== "closed" ? (
                  <Btn variant="danger" onClick={() => { const q = askDelete.q; setAskDelete(null); setConfirmStatus({ status: "closed", targets: [q], bulk: false }); }}>{t("queues.home.closeInstead", "Close queue instead")}</Btn>
                ) : null}
              </>
            : <>
                <Btn variant="secondary" onClick={() => setAskDelete(null)}>{t("common.actions.cancel", "Cancel")}</Btn>
                <Btn variant="danger" testid="queues-delete-confirm-btn" disabled={askDelete.busy} onClick={() => { void runDelete(); }}>{askDelete.busy ? t("queues.home.deleting", "Deleting…") : t("queues.home.deleteNow", "Delete")}</Btn>
              </>}>
          <p style={{ fontSize: 13, color: T.soft, lineHeight: 1.55, margin: 0 }}>
            {!askDelete.refusal
              ? t("queues.home.deleteBodyPermanent", "This deletes the queue permanently and can't be undone. Message boards showing it lose it. A queue that has taken customers cannot be deleted — close it instead.")
              : askDelete.refusal.reason === "linked_events"
                ? t("queues.home.deleteLinked", "It is still linked from {n} event(s). Remove it from each event's linked queues first, then delete it.").replace("{n}", String(askDelete.refusal.linkedEvents))
                : t("queues.home.deleteHistory", "It has taken customers, so its entries and reports still point at it. Close it instead: a closed queue stays in the list and takes no one.")}
          </p>
        </Modal>
      ) : null}
      {confirmStatus ? (() => {
        const closing = confirmStatus.status === "closed";
        const targets = confirmStatus.targets;
        const waiting = targets.reduce((s, q) => s + q.waiting, 0);
        const verb = closing ? t("queues.home.confirmVerbClose", "Close") : t("queues.home.confirmVerbPause", "Pause");
        const names = targets.map((q) => q.name).join(", ");
        const busyKey = confirmStatus.bulk ? `bulk:${confirmStatus.status}` : `st:${targets[0]?.id ?? ""}`;
        return (
          <Modal testid="queues-status-confirm-modal" tone="danger" onClose={() => setConfirmStatus(null)}
            title={confirmStatus.bulk
              ? t("queues.home.confirmVerbCount", "{verb} {count, plural, one {# queue} other {# queues}}?", { verb, count: targets.length })
              : t("queues.home.title2", "{verb} {arg2}?", { verb, arg2: t("queues.home.confirmNounQueue", "queue") })}
            sub={names}
            icon={closing ? <Icon.x size={16} /> : <Icon.clock size={16} />}
            footer={<>
              <Btn variant="secondary" onClick={() => setConfirmStatus(null)}>{t("common.actions.cancel", "Cancel")}</Btn>
              <Btn variant="danger" testid="queues-status-confirm-btn" disabled={busy === busyKey || targets.length === 0}
                onClick={() => {
                  const next = confirmStatus;
                  setConfirmStatus(null);
                  if (next.bulk) bulkStatus(next.status);
                  else if (next.targets[0]) changeStatus(next.targets[0], next.status);
                }}>
                {busy === busyKey
                  ? (closing ? t("queues.home.confirmClosing", "Closing\u2026") : t("queues.home.confirmPausing", "Pausing\u2026"))
                  : t("queues.home.text14", "{verb} {arg2}", { verb, arg2: t("queues.home.confirmNow", "now") })}
              </Btn>
            </>}>
            <p style={{ fontSize: 13, color: T.soft, lineHeight: 1.55, margin: 0 }}>
              {closing
                ? t("queues.home.confirmCloseBody", "Closing stops new joins and ends the line. The public join link stops accepting customers immediately.")
                : t("queues.home.confirmPauseBody", "Pausing stops new joins. People already waiting stay in line and can still be served.")}
            </p>
            {waiting > 0 ? (
              <p style={{ fontSize: 13, color: T.n1, lineHeight: 1.55, margin: 0 }}>
                <b>{waiting}</b>{" "}
                {waiting === 1 ? t("queues.home.confirmPersonWaiting", "person is waiting right now.") : t("queues.home.confirmPeopleWaiting", "people are waiting right now.")}{" "}
                {t("queues.home.confirmUseConcierge", "To move them to another queue or message them first, use Pause/Close in Concierge instead.")}
              </p>
            ) : null}
          </Modal>
        );
      })() : null}
    </Screen>
  );
}
